// pages/EmployeeLoanListing.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Banknote,
  Search,
  Plus,
  RefreshCcw,
  Eye,
  Building2,
  User,
  Hash,
  RotateCcw,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const EmployeeLoanListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // ✅ consistent button colors
  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  // filters
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [branch, setBranch] = useState("");

  // dropdown data
  const [branches, setBranches] = useState([]);

  // autocomplete suggestions
  const [empCodeSuggestions, setEmpCodeSuggestions] = useState([]);
  const [empNameSuggestions, setEmpNameSuggestions] = useState([]);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-21", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // Load dropdown data
  const loadDropdowns = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      const branchRes = await axios.get(`${BASE_URL}/api/employee-loan-listing/branches`, {
        params: {
          finYearId: Number(finYearId),
          branchId: branchId,
          branchType: branchType,
        },
      });

      if (branchRes?.data?.success) {
        setBranches(branchRes.data.rows || []);
        setBranch(branchId);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load dropdown data";
      toast.error(msg);
    }
  };

  // Employee Code Autocomplete
  const handleEmpCodeChange = async (value) => {
    setEmpCode(value);

    if (value.length >= 1) {
      try {
        const res = await axios.get(`${BASE_URL}/api/employee-loan-listing/autocomplete/empcode`, {
          params: {
            finYearId: Number(finYearId),
            search: value,
            branchId: branchId,
            branchType: branchType,
          },
        });

        if (res?.data?.success) {
          setEmpCodeSuggestions(res.data.rows || []);
        }
      } catch (err) {
        console.error("Error fetching employee codes:", err);
      }
    } else {
      setEmpCodeSuggestions([]);
    }
  };

  // Employee Name Autocomplete
  const handleEmpNameChange = async (value) => {
    setEmpName(value);

    if (value.length >= 1) {
      try {
        const res = await axios.get(`${BASE_URL}/api/employee-loan-listing/autocomplete/empname`, {
          params: {
            finYearId: Number(finYearId),
            search: value,
            branchId: branchId,
            branchType: branchType,
          },
        });

        if (res?.data?.success) {
          setEmpNameSuggestions(res.data.rows || []);
        }
      } catch (err) {
        console.error("Error fetching employee names:", err);
      }
    } else {
      setEmpNameSuggestions([]);
    }
  };

  // Fetch listing
  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-loan-listing`, {
        params: {
          finYearId: Number(finYearId),
          branchId: branchId,
          branchType: branchType,
          site: branch && branch !== "Select" ? branch : "",
          empCode: empCode.trim(),
          empName: empName.trim(),
          page: p,
          pageSize: pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load listing.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (branch) {
      fetchList(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch]);

  useEffect(() => {
    if (branch) {
      fetchList(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleViewAll = () => {
    setEmpCode("");
    setEmpName("");
    setBranch(branchId);
    setPage(1);
    fetchList(1);
  };

  const handleAddNew = () => {
    window.location.href = "/PayRoll/EmployeeLoanEntry?id=null";
  };

  const handleView = (loanId) => {
    window.location.href = `/PayRoll/EmployeeLoanEntry?id=${loanId}`;
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      {/* ✅ Toast outside container */}
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE LOAN LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Banknote className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Employee Loan Management
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  View / Add / Edit Employee Loans
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleAddNew} className={primaryBtn}>
                <Plus className="w-4 h-4" />
                Add New
              </button>
            </div>
          </div>

          {/* Search Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-5 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Employee Code */}
              <div>
                <label className={labelBase}>Employee Code</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Hash className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={empCode}
                    onChange={(e) => handleEmpCodeChange(e.target.value)}
                    placeholder="Search by code..."
                    className={`pl-11 ${inputBase}`}
                    list="empcode-suggestions"
                  />
                  {empCodeSuggestions.length > 0 && (
                    <datalist id="empcode-suggestions">
                      {empCodeSuggestions.map((code, idx) => (
                        <option key={idx} value={code} />
                      ))}
                    </datalist>
                  )}
                </div>
              </div>

              {/* Branch */}
              <div>
                <label className={labelBase}>Branch</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Building2 className="w-4 h-4 text-gray-400" />
                  </div>
                  <select value={branch} onChange={(e) => setBranch(e.target.value)} className={`pl-11 ${inputBase}`}>
                    <option value="Select">Select</option>
                    {branches.map((b, idx) => (
                      <option key={idx} value={b.fld_Branchid || b.value}>
                        {b.fld_BranchName || b.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Employee Name */}
              <div>
                <label className={labelBase}>Employee Name</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={empName}
                    onChange={(e) => handleEmpNameChange(e.target.value)}
                    placeholder="Search by name..."
                    className={`pl-11 ${inputBase}`}
                    list="empname-suggestions"
                  />
                  {empNameSuggestions.length > 0 && (
                    <datalist id="empname-suggestions">
                      {empNameSuggestions.map((name, idx) => (
                        <option key={idx} value={name} />
                      ))}
                    </datalist>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-4 justify-center">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>

              <button type="button" onClick={handleViewAll} className={ghostBtn}>
                <RotateCcw className="w-4 h-4" />
                View All
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-left font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Loan Amount</th>
                    <th className="px-4 py-4 text-center font-bold">Branch</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 5 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={r.fld_Loan_ID || idx}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => handleView(r.fld_Loan_ID)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-semibold underline"
                          >
                            {r.fld_Employeecode}
                          </button>
                        </td>
                        <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_Employee}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_LoanAmount}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_BranchName}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom row: pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeLoanListing;